import type { Filters } from "@superbright/indexeddb-orm";

/** Built-in filter keys always present in the embed toolbar. */
export const STRUCTURAL_FILTER_KEYS = new Set([
  "date_availability",
  "qty_bedrooms",
  "base_price",
]);

/** Keys that are never treated as dynamic tag groups. */
const NON_TAG_GROUP_KEYS = new Set([
  ...STRUCTURAL_FILTER_KEYS,
  "highlights",
  "preferredEnvironment",
  "spaceUsage",
  "amenities",
  "nearBuilding",
  "nearNeighbourhood",
  "page",
  "sortBy",
  "limit",
]);

/** Map legacy questionnaire body_key aliases to store keys. */
export const normalizeBodyKey = (bodyKey?: string): string => {
  switch (bodyKey) {
    case "availability":
      return "date_availability";
    case "bedrooms":
      return "qty_bedrooms";
    case "cost":
      return "base_price";
    default:
      return bodyKey ?? "";
  }
};

type FilterOptionLike = {
  value?: string | number | string[];
  key?: string;
  label?: string;
};

type FilterDefLike = {
  name?: string;
  body_key?: string;
  component?: string;
  options?: FilterOptionLike[];
};

/** True when a filter def is a tag multiselect that funnels into `highlights`. */
export const isTagFilterGroup = (def: FilterDefLike | undefined): boolean => {
  if (!def) return false;
  if (def.component === "slider") return false;
  const bodyKey = normalizeBodyKey(def.body_key ?? def.name);
  return bodyKey === "highlights" || def.name === "highlights";
};

const optionTagValues = (options: FilterOptionLike[] | undefined): string[] => {
  if (!options?.length) return [];
  const out: string[] = [];
  for (const opt of options) {
    const raw = opt.value ?? opt.key;
    if (raw == null) continue;
    if (Array.isArray(raw)) {
      for (const v of raw) out.push(String(v));
    } else {
      out.push(String(raw));
    }
  }
  return out;
};

const toTagStrings = (selection: unknown): string[] => {
  if (selection == null) return [];
  const arr = Array.isArray(selection) ? selection : [selection];
  const out: string[] = [];
  for (const item of arr) {
    if (item == null) continue;
    if (typeof item === "string" || typeof item === "number") {
      out.push(String(item));
      continue;
    }
    if (typeof item === "object" && "value" in item) {
      const v = (item as { value: unknown }).value;
      if (Array.isArray(v)) {
        for (const x of v) if (x != null) out.push(String(x));
      } else if (v != null) {
        out.push(String(v));
      }
    }
  }
  return Array.from(new Set(out));
};

/**
 * Spread a flat `highlights` tag bag back into per-group store keys so each
 * filter pill can restore its own selection after URL/bootstrap hydrate.
 *
 * Important: the migrated "Highlights" group also uses the store key `highlights`,
 * so we must never leave the union of all groups under that key — each group only
 * keeps tags that appear in its own options.
 */
export const distributeHighlightsToGroups = (
  filters: Partial<Filters> | Record<string, unknown>,
  filtersMap: Record<string, FilterDefLike> | undefined
): Record<string, unknown> => {
  const draft: Record<string, unknown> = {
    ...(filters as Record<string, unknown>),
  };
  if (!filtersMap) return draft;

  const flatTags = new Set(toTagStrings(draft.highlights));

  for (const [key, def] of Object.entries(filtersMap)) {
    if (!isTagFilterGroup(def)) continue;
    const groupKey = def.name ?? key;
    const optionSet = new Set(optionTagValues(def.options));
    const current = toTagStrings(draft[groupKey]);

    if (current.length > 0) {
      // Keep this group's selection, but drop any tags that don't belong to it
      // (e.g. Sunny tags previously merged into the shared `highlights` key).
      const owned = current.filter((tag) => optionSet.has(tag));
      draft[groupKey] = owned.length ? owned : undefined;
      continue;
    }

    if (flatTags.size === 0) continue;
    const matched = [...optionSet].filter((tag) => flatTags.has(tag));
    draft[groupKey] = matched.length ? matched : undefined;
  }

  return draft;
};

/** Union every tag-group selection in the store into a flat tag list (for URL/API). */
export const collectHighlightsFromGroups = (
  filters: Partial<Filters> | Record<string, unknown>,
  filtersMap: Record<string, FilterDefLike> | undefined
): string[] => {
  const tags = new Set<string>();

  if (filtersMap) {
    for (const [key, def] of Object.entries(filtersMap)) {
      if (!isTagFilterGroup(def)) continue;
      const groupKey = def.name ?? key;
      const optionSet = new Set(optionTagValues(def.options));
      for (const tag of toTagStrings(
        (filters as Record<string, unknown>)[groupKey]
      )) {
        // Only count tags that belong to this group (guards the shared `highlights` key).
        if (optionSet.size === 0 || optionSet.has(tag)) tags.add(tag);
      }
    }
  } else {
    for (const tag of toTagStrings(
      (filters as Record<string, unknown>).highlights
    )) {
      tags.add(tag);
    }
    for (const [key, val] of Object.entries(
      filters as Record<string, unknown>
    )) {
      if (NON_TAG_GROUP_KEYS.has(key)) continue;
      for (const tag of toTagStrings(val)) tags.add(tag);
    }
  }

  return Array.from(tags);
};

/** True when the store has no meaningful filter selections (incl. dynamic groups). */
export const isFiltersAtDefaults = (
  filters: Partial<Filters> | Record<string, unknown>
): boolean => {
  const f = filters as Record<string, unknown>;
  for (const val of Object.values(f)) {
    if (val == null) continue;
    return false;
  }
  return true;
};
